import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Axis } from '@visx/axis';
import { Group } from '@visx/group';
import { ParentSize } from '@visx/responsive';
import { scaleLinear } from '@visx/scale';
import { Line } from '@visx/shape';
import { Text } from '@visx/text';

import './LineChart.css';

function LineChart({ data, referenceRange }) {
  const cacheData = useRef({ yAxisValues: 0, min: 0, max: 0, refTop: 0, refBottom: 0 });

  const { yAxisValues, min, max, refTop, refBottom } = useMemo(() => {
    if (!data?.length)
      return {
        yAxisValues: cacheData.current.yAxisValues,
        min: cacheData.current.min,
        max: cacheData.current.max,
        refTop: cacheData.current.refTop,
        refBottom: cacheData.current.refBottom,
      };
    let min = Math.min(...data.map((item) => item.value), ...(referenceRange ? [referenceRange.bottom] : []));
    let max = Math.max(...data.map((item) => item.value), ...(referenceRange ? [referenceRange.top] : []));
    if (max - min === 0) {
      const center = max;
      min = center - center * 0.1;
      max = center + center * 0.1;
    }

    const range = max - min;
    const targetTicks = 5;
    const rawInterval = range / targetTicks;
    const magnitude = 10 ** Math.floor(Math.log10(rawInterval));
    const base = rawInterval / magnitude;
    let tickInterval = 0;
    if (base <= 1) tickInterval = magnitude * 1;
    else if (base <= 2) tickInterval = magnitude * 2;
    else if (base <= 5) tickInterval = magnitude * 5;
    else tickInterval = magnitude * 10;

    let ticks = [];
    let yMin = Math.floor(min / tickInterval) * tickInterval;
    let yMax = Math.ceil(max / tickInterval) * tickInterval;

    while (!(ticks.length >= 4 && ticks.length <= 6)) {
      yMin = Math.floor(min / tickInterval) * tickInterval;
      yMax = Math.ceil(max / tickInterval) * tickInterval;

      const decimals = Math.max(0, -Math.floor(Math.log10(tickInterval)));

      ticks = Array.from({ length: Math.round((yMax - yMin) / tickInterval) + 1 }, (_, i) =>
        Number((yMin + tickInterval * i).toFixed(decimals)),
      );

      if (ticks.length > 6) tickInterval *= 2;
      else if (ticks.length < 4) tickInterval /= 2;
    }

    const refTop =
      typeof referenceRange?.top === 'number' &&
      referenceRange?.top <= ticks[ticks.length - 1] &&
      referenceRange?.top >= ticks[0]
        ? {
            showAxisLabel: true,
            value: referenceRange?.top,
          }
        : referenceRange?.top > ticks[ticks.length - 1]
          ? {
              showAxisLabel: false,
              value: ticks[ticks.length - 1],
            }
          : {
              showAxisLabel: false,
              value: ticks[0],
            };

    const refBottom =
      typeof referenceRange?.bottom === 'number' &&
      referenceRange?.bottom <= ticks[ticks.length - 1] &&
      referenceRange?.bottom >= ticks[0]
        ? {
            showAxisLabel: true,
            value: referenceRange?.bottom,
          }
        : referenceRange?.bottom > ticks[ticks.length - 1]
          ? {
              showAxisLabel: false,
              value: ticks[ticks.length - 1],
            }
          : {
              showAxisLabel: false,
              value: ticks[0],
            };
    cacheData.current = { yAxisValues: ticks, min: yMin, max: yMax, refTop, refBottom };
    return { yAxisValues: ticks, min: yMin, max: yMax, refTop, refBottom };
  }, [data, referenceRange]);

  const referenceAxisValues = useMemo(() => {
    return [...(refTop.showAxisLabel ? [refTop.value] : []), ...(refBottom.showAxisLabel ? [refBottom.value] : [])];
  }, [refTop, refBottom]);

  const margin = useMemo(() => {
    const axisWidth = Math.max(
      ...yAxisValues.map((datum) => {
        const text = document.createElement('span');
        text.textContent = `${datum}`;
        text.style.fontSize = '12px';
        text.style.fontWeight = '500';
        text.style.width = 'fit-content';
        text.style.opacity = 0;
        text.style.pointerEvents = 'none';
        text.style.position = 'fixed';

        document.body.appendChild(text);
        const textWidth = Math.round(text.getBoundingClientRect().width) + 7;
        document.body.removeChild(text);
        return textWidth;
      }),
    );

    const referenceAxisWidth = referenceAxisValues.length
      ? Math.max(
          ...referenceAxisValues.map((datum) => {
            const text = document.createElement('span');
            text.textContent = `${datum}`;
            text.style.fontSize = '12px';
            text.style.fontWeight = '500';
            text.style.width = 'fit-content';
            text.style.opacity = 0;
            text.style.pointerEvents = 'none';
            text.style.position = 'fixed';

            document.body.appendChild(text);
            const textWidth = Math.round(text.getBoundingClientRect().width) + 7;
            document.body.removeChild(text);
            return textWidth;
          }),
        )
      : 0;

    return {
      top: 8,
      left: axisWidth,
      right: referenceAxisWidth,
      bottom: 42,
    };
  }, [yAxisValues, referenceAxisValues]);

  const [parentWidth, setParentWidth] = useState(0);
  const [pSize, setPSize] = useState(0);
  useEffect(() => {
    handleTouchEnd();
  }, [pSize]);

  useEffect(() => {
    let value = Math.floor((parentWidth - margin.left - margin.right - 44) / 50);
    if (value >= data.length) {
      value = data.length;
    }
    setPSize(value);
  }, [parentWidth]);
  const [throttle, setThrottle] = useState(false);

  function handleResize() {
    if (throttle) return;
    if (!throttle) {
      setThrottle(true);
      setTimeout(async () => {
        setParentWidth(containerRef.current.parentNode.getBoundingClientRect().width);
        setThrottle(false);
      }, 300);
    }
  }
  useEffect(() => {
    setParentWidth(containerRef.current.parentNode.getBoundingClientRect().width);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const containerRef = useRef(null);

  const [selectedIndex, setSelectedIndex] = useState(data.length - 1);

  useEffect(() => {
    setSelectedIndex(data.length - 1);
    setScroll(0);
  }, [data]);

  const [scroll, setScroll] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);

  const x1 = useRef(0);
  const x2 = useRef(0);

  function getSizeData(innerWidth) {
    const padding = {
      left: innerWidth / pSize / 2,
      right: innerWidth / pSize / 2,
    };
    const innerWidthWithoutPadding = innerWidth - (padding?.left ?? 0) - (padding?.right ?? 0);
    let lastDataPosition = innerWidth - (padding?.right ?? 0);
    let dataGap = innerWidthWithoutPadding / (pSize > 1 ? pSize - 1 : 1);

    return { lastDataPosition, dataGap };
  }

  function handleTouchStart(e) {
    x1.current = e.touches[0].clientX;
    setIsScrolling(true);
  }

  function handleTouchMove(e) {
    if (!containerRef.current) return;

    if (pSize >= data.length) return;
    const innerWidth = containerRef.current.getBoundingClientRect().width - margin.left - margin.right;

    x2.current = e.touches[0].clientX;

    const MOVE_DIRECTION = x2.current < x1.current ? 'right' : 'left';
    const MOVE_DISTANCE = x2.current - x1.current;

    const { dataGap } = getSizeData(innerWidth);

    x1.current = x2.current;

    if (MOVE_DIRECTION === 'right' && scroll + MOVE_DISTANCE <= 0 - dataGap) {
      setScroll(0 - dataGap);
      return;
    } else if (MOVE_DIRECTION === 'left' && scroll + MOVE_DISTANCE >= dataGap * (data.length - pSize) + dataGap) {
      setScroll(dataGap * (data.length - pSize) + dataGap);
      return;
    }
    setSelectedIndex(null);

    setScroll(scroll + MOVE_DISTANCE);
  }

  function handleTouchEnd() {
    if (!containerRef.current) return;

    setIsScrolling(false);

    const innerWidth = containerRef.current.getBoundingClientRect().width - margin.left - margin.right;

    const { dataGap } = getSizeData(innerWidth);

    const scrollValueList = [];
    for (let i = 0; i <= data.length - pSize; i++) {
      scrollValueList.push(i * dataGap);
    }

    const closestScrollValue = scrollValueList.reduce((prev, curr) => {
      return Math.abs(curr - scroll) < Math.abs(prev - scroll) ? curr : prev;
    }, scrollValueList[0]);

    setScroll(closestScrollValue || 0);
  }

  return (
    <>
      <article
        ref={containerRef}
        className={`container ${isScrolling ? 'scrolling' : ''}`}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <ParentSize debounceTime={20}>
          {(parent) => {
            const width = parent.width;
            const height = parent.height;
            if (width === 0 || height === 0 || pSize === 0) return null;

            const innerWidth = width - margin.left - margin.right;
            const innerHeight = height - margin.top - margin.bottom;

            const { lastDataPosition, dataGap } = getSizeData(innerWidth);

            const totalContentWidth = dataGap * (data.length - 1);

            const startX = lastDataPosition - totalContentWidth;

            const xScale = scaleLinear({
              domain: [0, data.length - 1],
              range: [startX, startX + totalContentWidth],
            });
            const yScale = scaleLinear({
              domain: [min, max],
              range: [innerHeight, 0],
            });

            return (
              <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
                <clipPath id='chart-content-clip'>
                  <rect x='0' y='-100' width={innerWidth} height={innerHeight + 150} />
                </clipPath>

                <Group top={margin.top + innerHeight} left={margin.left} clipPath='url(#chart-content-clip)'>
                  <Group top={0} left={0} style={{ transform: `translateX(${scroll}px)` }}>
                    {data.map((datum, idx) => {
                      const isSelected = idx === selectedIndex;
                      return (
                        <React.Fragment key={idx}>
                          {isSelected && (
                            <rect x={xScale(idx) - 20.5} y={4} width={41} height={36} fill='#F0F2F5' rx={8}></rect>
                          )}
                          {datum.xAxisLabel.split('<br />').map((word, index) => {
                            return (
                              <React.Fragment key={index}>
                                <Text
                                  y={10 + index * 14}
                                  x={xScale(idx)}
                                  fontSize={12}
                                  textAnchor='middle'
                                  verticalAnchor='start'
                                  fontWeight={'500'}
                                  fill={isSelected ? '#24272C' : '#7E848C'}
                                >
                                  {word}
                                </Text>
                              </React.Fragment>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </Group>
                </Group>
                <Group top={margin.top}>
                  {yAxisValues.map((val, idx) => (
                    <Line
                      key={idx}
                      from={{
                        x: margin.left,
                        y: yScale(val),
                      }}
                      to={{
                        x: width - margin.right,
                        y: yScale(val),
                      }}
                      stroke='#D5D9E0'
                      strokeDasharray={idx === 0 ? '0' : '2'}
                    />
                  ))}
                  {referenceAxisValues.map((val, idx) => (
                    <Line
                      key={idx}
                      from={{
                        x: margin.left,
                        y: yScale(val),
                      }}
                      to={{
                        x: width - margin.right,
                        y: yScale(val),
                      }}
                      stroke='#18B00D'
                      strokeDasharray={'2'}
                    />
                  ))}
                </Group>
                <Group top={margin.top} left={margin.left}>
                  <Axis
                    scale={yScale}
                    orientation='left'
                    numTicks={5}
                    hideTicks
                    tickValues={yAxisValues}
                    hideAxisLine
                    tickFormat={(val) => val + ''}
                    tickLabelProps={{
                      fill: '#24272C',
                      fontSize: 12,
                      textAnchor: 'end',
                      verticalAnchor: 'middle',
                    }}
                  />
                </Group>
                <Group top={margin.top} left={innerWidth + margin.left} className='no-animation'>
                  {(() => {
                    return (
                      <>
                        <Axis
                          scale={yScale}
                          orientation='center'
                          hideTicks
                          tickValues={referenceAxisValues}
                          hideAxisLine
                          tickFormat={(val) => val + ''}
                          tickLabelProps={{
                            fill: '#18B00D',
                            fontSize: 12,
                            textAnchor: 'start',
                            verticalAnchor: 'middle',
                          }}
                        />
                      </>
                    );
                  })()}
                </Group>
                {(() => {
                  const refHeight = yScale(refBottom.value) - yScale(refTop.value);
                  if (refHeight <= 0) return null;
                  return (
                    <Group top={margin.top} left={margin.left}>
                      <rect
                        x={0}
                        y={yScale(refTop.value)}
                        width={innerWidth}
                        height={refHeight}
                        fill={'rgba(108, 218, 100, 0.10)'}
                      />
                    </Group>
                  );
                })()}
                <Group top={margin.top} left={margin.left} clipPath='url(#chart-content-clip)'>
                  <Group top={0} left={0} style={{ transform: `translateX(${scroll}px)` }}>
                    {data.slice(0, -1).map((datum, idx) => (
                      <Line
                        key={idx}
                        from={{
                          x: xScale(idx),
                          y: yScale(datum.value),
                        }}
                        to={{
                          x: xScale(idx + 1),
                          y: yScale(data[idx + 1].value),
                        }}
                        stroke={'#18B00D'}
                        strokeWidth={2}
                      />
                    ))}
                    {data.map((datum, idx) =>
                      idx === selectedIndex ? (
                        <Line
                          key={idx}
                          from={{
                            x: xScale(idx),
                            y: yScale(datum.value),
                          }}
                          to={{
                            x: xScale(idx),
                            y: yScale(min),
                          }}
                          stroke={'#D5D9E0'}
                          strokeWidth={1}
                        />
                      ) : null,
                    )}
                    {data.map((datum, idx) => {
                      return (
                        <React.Fragment key={idx}>
                          {(() => {
                            const isSelected = idx === selectedIndex;
                            return (
                              <>
                                {isSelected && (
                                  <circle
                                    cx={xScale(idx)}
                                    cy={yScale(datum.value)}
                                    r={19}
                                    fill={'rgba(24, 176, 13, 0.20)'}
                                    stroke={'rgba(24, 176, 13, 0.20)'}
                                    strokeWidth={0}
                                  ></circle>
                                )}
                                <circle
                                  cx={xScale(idx)}
                                  cy={yScale(datum.value)}
                                  r={7}
                                  fill={isSelected ? 'white' : '#18B00D'}
                                  stroke={isSelected ? '#18B00D' : 'white'}
                                  strokeWidth={isSelected ? 2.333 : 2}
                                ></circle>
                              </>
                            );
                          })()}
                        </React.Fragment>
                      );
                    })}

                    {data.map((datum, idx) => {
                      const isSelected = idx === selectedIndex;

                      const isScreenFirstIndex =
                        selectedIndex !== null ? Math.abs((data.length - pSize - idx) * dataGap - scroll) < 1 : false;

                      const isScreenLastIndex = isSelected
                        ? Math.abs(scroll / dataGap - (data.length - 1 - idx)) < 0.9
                        : false;

                      const text = document.createElement('span');

                      text.textContent = `${datum.value} ${datum.unit}`;
                      text.style.fontSize = '16px';
                      text.style.fontWeight = '500';
                      text.style.width = 'fit-content';
                      document.querySelector('.svg-trash')?.appendChild(text);

                      const textWidth = text.getBoundingClientRect().width;

                      document.querySelector('.svg-trash')?.removeChild(text);

                      const boxPadding = 7.3;

                      let boxWidth = textWidth + boxPadding * 2;

                      const isNeedAdjustLocation = boxWidth > innerWidth / pSize;

                      return (
                        <React.Fragment key={idx}>
                          {isSelected && (
                            <rect
                              x={(() => {
                                let valueCorrection = -(boxWidth / 2);
                                if (isNeedAdjustLocation) {
                                  if (isScreenFirstIndex) valueCorrection = 0 - dataGap / 2;
                                  else if (isScreenLastIndex) valueCorrection = -boxWidth + dataGap / 2;
                                }
                                return xScale(idx) + valueCorrection;
                              })()}
                              y={yScale(datum.value) - 56}
                              width={boxWidth}
                              height={33}
                              fill={'#24272C'}
                              rx={8}
                            ></rect>
                          )}
                          {isSelected && (
                            <Text
                              x={(() => {
                                let valueCorrection = 0;
                                if (isNeedAdjustLocation) {
                                  if (isScreenFirstIndex) valueCorrection = 0 - dataGap / 2 + boxPadding;
                                  else if (isScreenLastIndex) valueCorrection = -boxWidth + dataGap / 2 + boxPadding;
                                }
                                return xScale(idx) + valueCorrection;
                              })()}
                              y={yScale(datum.value) - 34}
                              textAnchor={
                                isNeedAdjustLocation && (isScreenLastIndex || isScreenFirstIndex) ? 'start' : 'middle'
                              }
                              verticalAnchor='end'
                              fontSize={16}
                              fontWeight={'500'}
                              fill={'#FFF'}
                            >
                              {`${datum.value} ${datum.unit}`}
                            </Text>
                          )}
                        </React.Fragment>
                      );
                    })}
                    {data.map((_, idx) => (
                      <rect
                        key={idx}
                        x={xScale(idx) - innerWidth / pSize / 2}
                        y={0}
                        width={innerWidth / pSize}
                        height={innerHeight + margin.bottom}
                        opacity={0}
                        onClick={() => {
                          setSelectedIndex(idx);
                        }}
                      ></rect>
                    ))}
                  </Group>
                </Group>
              </svg>
            );
          }}
        </ParentSize>
        <div
          className='svg-trash'
          style={{
            position: 'absolute',
            opacity: 0,
            pointerEvents: 'none',
            width: '100%',
            height: '30px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        ></div>
      </article>
    </>
  );
}
export default LineChart;

